/**
 * Naming of a `.zip` a space is handed over in, per 20.2.3 §6: `CapQuest_[Space_name]`.
 *
 * The name comes from the row, never from a storage key — the keys deliberately carry no names, so
 * that renaming a folder never touches an object. Spaces are squeezed to underscores to match the
 * form the specification writes, and the characters a name may not hold anyway
 * (`hasForbiddenNameCharacters`) are dropped rather than refused: a room created before that rule
 * still has to be downloadable.
 */
const UNSAFE_FILENAME_CHARACTERS = /[\\/:*?"<>|]/g;

const WHITESPACE_RUN = /\s+/g;

// Long enough for any room anybody names, short of the 255 bytes a filesystem stops at.
export const SPACE_ARCHIVE_NAME_MAX_LENGTH = 80;

export const SPACE_ARCHIVE_CONTENT_TYPE = 'application/zip';

/** Written into an archive that lost files on the way, so a gap is never silently a gap. */
export const SPACE_ARCHIVE_MISSING_REPORT_NAME = '_missing-files.txt';

export const spaceArchiveFilename = (name: string): string => {
  const safe =
    name
      .replace(UNSAFE_FILENAME_CHARACTERS, '')
      .trim()
      .replace(WHITESPACE_RUN, '_')
      .slice(0, SPACE_ARCHIVE_NAME_MAX_LENGTH) || 'archive';

  return `CapQuest_${safe}.zip`;
};

/**
 * Where one file sits inside the archive.
 *
 * A segment of nothing but dots would be a path that climbs out of the directory it was extracted
 * into: names cannot hold a slash (`hasForbiddenNameCharacters`), but `..` is a legal folder name,
 * so it is neutered here rather than trusted.
 */
const DOT_ONLY_SEGMENT = /^\.+$/;

export const spaceArchiveEntryPath = (segments: string[]): string =>
  segments
    .filter((segment) => segment.length > 0)
    .map((segment) => (DOT_ONLY_SEGMENT.test(segment) ? '_' : segment))
    .join('/');
